import type { ApplicationData, SubmitResult } from '@policygenius/ai-assistant-shared';
import { AiAssistant as AiAssistantComponent } from '@policygenius/ai-assistant-ui';
import React, { useCallback, useContext } from 'react';

// eslint-disable-next-line import/no-unassigned-import -- compiled Tailwind/global styles from the UI package
import '@policygenius/ai-assistant-ui/styles.css';

import { PaymentMode } from 'containers/App/constants';
import { useUpdateBasicInfoMutation } from 'containers/Basics/graphql/mutations/UpdateBasicInfo';
import { useUpdateHouseholdInformationMutation } from 'containers/CommonApp/HouseholdInformation/graphql/mutations/HouseholdInformationMutation';
import { useSelectMyQuotesPolicyMutation } from 'containers/MyQuotes/graphql/SelectMyQuotesPolicy';
import { AuthContext } from 'contexts/Auth';
import { useSaveEmailMutation } from 'graphql/mutations/customers/SaveEmail/mutation';
import { useAddInternalCaseTagsMutation } from 'graphql/mutations/lifeApplications/addInternalCaseTags/mutation';
import { usePolicyPreferencesUpdateMutation } from 'graphql/mutations/lifeApplications/policy/policyPreferencesUpdate/mutation';
import { useSubmitCommonAppMutation } from 'graphql/mutations/lifeApplications/submitCommonApp/mutation';
import useSelector from 'store/useSelector';

const API_URL = process.env.LIFE_BFF_BASE_URL || '';

const AiAssistant: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const activeLifeApplicationId = useSelector((app) => app.activeLifeApplicationId);
  const activeUserUuid = useSelector((app) => app.activeUserUuid);
  const [saveIdentityProfile] = useSaveEmailMutation();
  const [updateBasicInfo] = useUpdateBasicInfoMutation();
  const [updatePolicyPreferences] = usePolicyPreferencesUpdateMutation();
  const [selectMyQuotesPolicy] = useSelectMyQuotesPolicyMutation();
  const [updateHouseholdInformation] = useUpdateHouseholdInformationMutation();
  const [submitCommonApp] = useSubmitCommonAppMutation();
  const [addInternalCaseTags] = useAddInternalCaseTagsMutation();
  const bearerToken = auth.type !== 'none' && auth.token ? auth.token : undefined;

  console.log('activeLifeApplicationId', activeLifeApplicationId);
  console.log('activeUserUuid', activeUserUuid);

  const handleApplicationReady = useCallback(
    async (data: ApplicationData): Promise<SubmitResult> => {
      const { profile } = data;

      console.log('------------AFTER AI ASSISTANT CALL--------------------');
      console.log('activeLifeApplicationId', activeLifeApplicationId);
      console.log('activeUserUuid', activeUserUuid);
      console.log('profile', profile);
      console.log('data', data);

      try {
        await saveIdentityProfile({
          variables: {
            input: {
              customer_id: activeUserUuid,
              email: profile.email,
              first_name: profile.firstName,
              last_name: profile.lastName,
              phone: profile.phone,
            },
          },
        });

        await updateBasicInfo({
          variables: {
            uuid: activeUserUuid,
            dateOfBirth: profile.dob,
            gender: profile.gender,
            zipCode: profile.zipCode ?? '',
          },
        });

        await updatePolicyPreferences({
          variables: {
            input: {
              customer_id: activeUserUuid,
              individual_income: profile.annualIncome,
            },
          },
        });

        if (data.selectedQuote) {
          const { selectedQuote } = data;

          await selectMyQuotesPolicy({
            variables: {
              policyInput: {
                application_id: activeLifeApplicationId,
                triggered_from: 'AI Assistant',
                carrier_slug: selectedQuote.carrier,
                carrier_name: selectedQuote.carrierName,
                coverage_amount: selectedQuote.coverageAmount,
                payment_mode: PaymentMode.MONTHLY,
                annual_premium: selectedQuote.annualPremium,
                monthly_premium: selectedQuote.monthlyPremium,
                issue_type: selectedQuote.issueType,
                product_urn: selectedQuote.productUrn,
                policy_name: selectedQuote.productName,
                term_in_years: selectedQuote.termYears,
                underwriting_class: selectedQuote.healthCategory,
                select_and_apply: true,
                table_rating: selectedQuote.tableRating.toString(),
                product_type: selectedQuote.productType,
                plan_identifier_name: selectedQuote.planIdentifierName,
                tobacco: selectedQuote.isTobacco,
                underwriting_class_group: selectedQuote.underwritingClass,
                issue_age: selectedQuote.issueAge,
              },
              preferencesInput: {
                customer_id: activeUserUuid,
                coverage_amount: selectedQuote.coverageAmount,
                term: String(selectedQuote.termYears),
              },
              caseTagsInput: {
                application_id: activeLifeApplicationId,
                internal_case_tags: ['AI-assisted Lead'],
              },
            },
          });
        } else if (data.noMatchReason === 'opt_out') {
          await addInternalCaseTags({
            variables: {
              input: {
                application_id: activeLifeApplicationId,
                internal_case_tags: [
                  'AI-assisted Lead',
                  'AI Flow- No Estimates',
                  'AI Opt-Out Flow',
                ],
              },
            },
          });
        } else if (data.noMatchReason === 'no_carriers') {
          await addInternalCaseTags({
            variables: {
              input: {
                application_id: activeLifeApplicationId,
                internal_case_tags: ['AI-assisted Lead', 'AI Flow- No Estimates'],
              },
            },
          });
        }

        if (profile.maritalStatus) {
          await updateHouseholdInformation({
            variables: {
              householdInput: {
                customer_id: activeUserUuid,
                marital_status: profile.maritalStatus,
              },
              formInput: {
                form_name: 'household_profile',
              },
            },
          });
        }

        await submitCommonApp({
          variables: {
            input: { application_id: activeLifeApplicationId },
          },
        });

        return { success: true, applicationId: activeLifeApplicationId };
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);

        return { success: false, error: message };
      }
    },
    [
      activeLifeApplicationId,
      activeUserUuid,
      saveIdentityProfile,
      updateBasicInfo,
      updatePolicyPreferences,
      selectMyQuotesPolicy,
      updateHouseholdInformation,
      submitCommonApp,
      addInternalCaseTags,
    ]
  );

  return (
    <AiAssistantComponent
      apiUrl={API_URL}
      bearerToken={bearerToken}
      onApplicationReady={handleApplicationReady}
    />
  );
};

export default AiAssistant;
